import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ArrowUpRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Logo } from "./Logo";
import { NAV, SITE } from "@/config/site";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      className={`sticky top-0 z-50 border-b transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-xl border-line shadow-[0_8px_30px_-20px_rgba(27,42,65,0.4)]" : "bg-white/80 backdrop-blur-md border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="container-x flex items-center justify-between h-[76px]">
        <Link to="/" aria-label="O'RIZON 360 — Accueil" data-testid="header-logo-link">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Navigation principale">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-link-${item.to === "/" ? "accueil" : item.to.slice(1)}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-semibold rounded-full transition-colors duration-200 ${
                  isActive ? "text-copper" : "text-slate hover:text-copper"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a href={SITE.phone.href} className="flex items-center gap-2 text-sm font-semibold text-slate hover:text-copper transition-colors" data-testid="header-phone-link">
            <Phone size={16} /> {SITE.phone.display}
          </a>
          <Link to="/contact" className="btn-primary !py-2.5 !px-5" data-testid="header-devis-cta">
            Devis gratuit <ArrowUpRight size={16} />
          </Link>
        </div>

        <button
          className="lg:hidden p-2 rounded-lg text-slate hover:bg-concrete"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            id="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden border-t border-line bg-white"
            aria-label="Navigation mobile"
            data-testid="mobile-menu"
          >
            <div className="container-x py-4 flex flex-col gap-1">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`mobile-nav-link-${item.to === "/" ? "accueil" : item.to.slice(1)}`}
                  className={({ isActive }) => `px-3 py-3 rounded-lg font-heading font-bold text-2xl uppercase ${isActive ? "text-copper" : "text-slate"}`}
                >
                  {item.label}
                </NavLink>
              ))}
              <Link to="/contact" className="btn-primary mt-3" data-testid="mobile-devis-cta">Demander un devis gratuit</Link>
              <a href={SITE.phone.href} className="btn-outline mt-2" data-testid="mobile-phone-link"><Phone size={16} /> {SITE.phone.display}</a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
